import { useEffect } from 'react'
import { router } from '@inertiajs/react'

export type ComparePeriod = { start: string; end: string }

// The compare window is per viewer, never stored on the plan. Precedence:
// URL (?past_start/?past_end) wins and is copied into localStorage; else the
// localStorage value for this plan is pushed into the URL so the server
// computes with it; else the server default (plan window one year earlier).
const ISO = /^\d{4}-\d{2}-\d{2}$/
const storageKey = (planId: string) => `topdown.compare.${planId}`

function readStored(planId: string): ComparePeriod | null {
  try {
    const raw = localStorage.getItem(storageKey(planId))
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<ComparePeriod>
    if (parsed.start && parsed.end && ISO.test(parsed.start) && ISO.test(parsed.end)) {
      return { start: parsed.start, end: parsed.end }
    }
  } catch {
    // storage unavailable or corrupt — fall through to the server default
  }
  return null
}

function writeStored(planId: string, period: ComparePeriod) {
  try {
    localStorage.setItem(storageKey(planId), JSON.stringify(period))
  } catch {
    // storage unavailable — the URL still carries the choice
  }
}

function readUrl(): ComparePeriod | null {
  const query = new URLSearchParams(window.location.search)
  const start = query.get('past_start')
  const end = query.get('past_end')
  return start && end && ISO.test(start) && ISO.test(end) ? { start, end } : null
}

type Options = {
  planId: string
  // Page path to reload with the new window (query params appended).
  path: string
  // Inertia partial-reload keys that depend on the compare window.
  only: string[]
  // What the server computed with on this render.
  current: ComparePeriod
}

export function useComparePeriod({ planId, path, only, current }: Options) {
  const navigate = (period: ComparePeriod, replace: boolean) =>
    router.get(
      path,
      { past_start: period.start, past_end: period.end },
      { preserveScroll: true, preserveState: true, only, replace },
    )

  useEffect(() => {
    const fromUrl = readUrl()
    if (fromUrl) {
      writeStored(planId, fromUrl)
      return
    }
    const stored = readStored(planId)
    if (stored && (stored.start !== current.start || stored.end !== current.end)) {
      navigate(stored, true)
    }
    // Run once per plan: later renders reflect what we navigated to.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planId])

  const apply = (period: ComparePeriod) => {
    writeStored(planId, period)
    navigate(period, false)
  }

  return { apply }
}

// Same calendar dates one year earlier (Feb 29 clamps to Feb 28).
export function shiftYear(iso: string, years: number): string {
  const [y, m, d] = iso.split('-').map(Number)
  const year = y + years
  const lastDay = new Date(Date.UTC(year, m, 0)).getUTCDate()
  const day = Math.min(d, lastDay)
  return `${year}-${String(m).padStart(2, '0')}-${String(day).padStart(2, '0')}`
}
